/**
 * Selection behaviour of the card, mirroring the anatomy's "Selection variant".
 * `SINGLE` renders a radio trailing control (one option per group);
 * `MULTIPLE` renders a checkbox trailing control (many options per group).
 */
export const SelectionCardMode = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
} as const;

export type SelectionCardMode =
  (typeof SelectionCardMode)[keyof typeof SelectionCardMode];

/**
 * Card surface fill, mirroring the anatomy's "Background variant".
 * `WHITE` maps to `surface.background.neutral.default.base`;
 * `GREY` to `surface.background.neutral.default.raised`.
 */
export const SelectionCardBackground = {
  WHITE: 'white',
  GREY: 'grey',
} as const;

export type SelectionCardBackground =
  (typeof SelectionCardBackground)[keyof typeof SelectionCardBackground];

export type SelectionCardProps = {
  selected?: boolean;
  value?: string | number;
  name?: string;
  title?: string;
  details?: string;
  disabled?: boolean;
  /**
   * Renders the focused visual state declaratively (focus ring), independent of
   * real keyboard focus. Useful for covering the Figma "focused" state and for
   * stories; native `:focus-visible` keeps working alongside it.
   */
  focused?: boolean;
  /**
   * Selection mode. `SINGLE` (default) → radio control; `MULTIPLE` → checkbox
   * control. The `ribbon`, `callout` and `body` slots are intended for
   * `SINGLE` only, per the component anatomy.
   */
  mode?: SelectionCardMode;
  /**
   * Card surface fill. `WHITE` (default) or `GREY`.
   */
  background?: SelectionCardBackground;
  ctaLabel?: string;
  tabindex?: number;
  testId?: string;
};

export type SelectionCardEmits = {
  'update:selected': [value: boolean];
  select: [value: string | number];
};

export type SelectionCardSlots = {
  ribbon?: () => unknown;
  badge?: () => unknown;
  leading?: () => unknown;
  title?: () => unknown;
  details?: () => unknown;
  callout?: () => unknown;
  body?: () => unknown;
  cta?: () => unknown;
};
